import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Popover,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import {
  Circle as CircleIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useSocket } from '../context/SocketContext';

const RealtimeIndicator = () => {
  const { isConnected, connectionError, reconnecting } = useSocket();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Determine status
  const getStatus = () => {
    if (connectionError) {
      return {
        label: 'Error',
        color: 'error',
        dotColor: '#f44336',
        description: connectionError,
      };
    }
    if (reconnecting) {
      return {
        label: 'Reconnecting',
        color: 'warning',
        dotColor: '#ff9800',
        description: 'Attempting to reconnect...',
      };
    }
    if (isConnected) {
      return {
        label: 'Connected',
        color: 'success',
        dotColor: '#4caf50',
        description: 'Real-time updates active',
      };
    }
    return {
      label: 'Disconnected',
      color: 'default',
      dotColor: '#9e9e9e',
      description: 'No real-time connection',
    };
  };

  const status = getStatus();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={`Real-time status: ${status.label}`}>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{
            padding: 0.5,
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <CircleIcon
            sx={{
              fontSize: 16,
              color: status.dotColor,
              animation: reconnecting ? 'pulse 1.5s ease-in-out infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': {
                  opacity: 1,
                },
                '50%': {
                  opacity: 0.5,
                },
              },
            }}
          />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 280, maxWidth: 350 }}>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon color="action" fontSize="small" />
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Real-time Connection
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Status:
              </Typography>
              <Chip
                label={status.label}
                color={status.color}
                size="small"
                icon={
                  <CircleIcon
                    sx={{
                      fontSize: 12,
                      color: `${status.dotColor} !important`,
                    }}
                  />
                }
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              {status.description}
            </Typography>

            {isConnected && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Active Features:
                </Typography>
                <Stack spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    • Inventory updates
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • New lead notifications
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • Sale status changes
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • Payment notifications
                  </Typography>
                </Stack>
              </Box>
            )}

            {connectionError && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                  Error Details:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {connectionError}
                </Typography>
              </Box>
            )}

            {reconnecting && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                <RefreshIcon sx={{ fontSize: 14, animation: 'spin 1s linear infinite' }} />
                <Typography variant="caption" color="text.secondary">
                  Reconnecting...
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>
      </Popover>

      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </Box>
  );
};

export default RealtimeIndicator;
